import { isDefined } from '@sgme/fp';
import { Column } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SecondaryCellProps, SecondaryCellRendererProps } from 'routes/secondary-market-batch/grid/types';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

type Props<TValue extends RowData[keyof RowData]> = SecondaryCellRendererProps<TValue> & {
  render: (value: SecondaryCellProps<TValue>) => React.ReactNode;
};

function usePosition(rowIndex: number | null, column?: Column) {
  return useMemo(() => {
    const colId = column?.getColId();
    if (isDefined(colId) && isDefined(rowIndex)) {
      return { colId, rowIndex };
    }
    return;
  }, [column, rowIndex]);
}
export function CellRendererAsEditorWrapper<TValue extends RowData[keyof RowData]>(p: Props<TValue>) {
  const position = usePosition(p.node.rowIndex, p.column);
  const ref = useRef<HTMLDivElement>(null);
  const handleEvent = useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      if (e.type === 'keyup') {
        const event = e as React.KeyboardEvent<HTMLDivElement>;
        p.api.startEditingCell({
          rowIndex: position!.rowIndex,
          colKey: position!.colId,
          key: event.key,
        });
      }
    },
    [position, p.api],
  );

  useEffect(() => {
    const focusedCell = p.api.getFocusedCell();
    if (
      focusedCell &&
      focusedCell?.column?.getColId() === position?.colId &&
      focusedCell?.rowIndex === position?.rowIndex
    ) {
      p.eGridCell.focus();
    }
  }, [p.api, position, p.eGridCell]);
  return (
    <div
      ref={ref}
      onClickCapture={handleEvent}
      onFocusCapture={handleEvent}
      onKeyDownCapture={handleEvent}
      onKeyUpCapture={handleEvent}
    >
      {p.render({ ...p, type: 'renderer' })}
    </div>
  );
}
